import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, ExternalLink, Sparkles, Linkedin } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { formatDate } from "@/lib/format";

export default function ArticlesPanel({ onEdit }) {
  const { lang, tr } = useLang();
  const a = tr("admin");
  const [articles, setArticles] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [busyPost, setBusyPost] = useState(null);
  const [postState, setPostState] = useState(null);

  const genPost = async (art) => {
    setBusyPost(art.id);
    try {
      const { data } = await api.post("/admin/ai/social-post", { slug: art.slug, lang }, { timeout: 240000 });
      setPostState({ slug: art.slug, post: data.post });
    } catch {
      toast.error(a.socialError);
    } finally {
      setBusyPost(null);
    }
  };

  const copyPost = async () => {
    try {
      await navigator.clipboard.writeText(postState.post);
      toast.success(a.postCopied);
    } catch {
      toast.error(a.socialError);
    }
  };

  const load = () => {
    api
      .get("/admin/articles")
      .then((r) => setArticles(r.data || []))
      .catch(() => setArticles([]));
  };

  useEffect(load, []);

  const togglePublish = async (art) => {
    const action = art.status === "published" ? "unpublish" : "publish";
    await api.post(`/admin/articles/${art.id}/${action}`);
    toast.success(action === "publish" ? a.published : a.unpublished);
    load();
  };

  const genVisual = async (art) => {
    setBusyId(art.id);
    try {
      await api.post(`/admin/articles/${art.id}/visual`, {}, { timeout: 240000 });
      toast.success(a.visualDone);
      load();
    } catch {
      toast.error(a.visualError);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (art) => {
    if (!window.confirm(a.confirmDelete)) return;
    await api.delete(`/admin/articles/${art.id}`);
    toast.success(a.deleted);
    load();
  };

  const btn = "px-3 py-2 border font-mono text-[10px] uppercase tracking-[0.15em] transition-colors inline-flex items-center gap-1.5";

  return (
    <div data-testid="admin-articles-panel">
      <div className="mb-8 flex items-center justify-between">
        <h2 className="font-serif text-2xl text-ivory">{a.tabs.articles}</h2>
        <button
          onClick={() => onEdit(null)}
          data-testid="admin-new-article-button"
          className="inline-flex items-center gap-2 bg-gold px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-gold-bright"
        >
          <Plus className="h-4 w-4" /> {a.newArticle}
        </button>
      </div>

      {articles === null ? (
        <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.25em] text-ivory/40">…</p>
      ) : articles.length === 0 ? (
        <p className="border border-gold/15 py-16 text-center text-sm text-ivory/50" data-testid="admin-articles-empty">
          {a.emptyArticles}
        </p>
      ) : (
        <div data-testid="admin-articles-list">
          {articles.map((art) => {
            const c = art[lang] || art.it;
            const published = art.status === "published";
            const publicUrl = `/${art.section === "knowledge" ? "knowledge-center" : "insights"}/${art.slug}`;
            return (
              <div
                key={art.id}
                data-testid={`admin-article-row-${art.slug}`}
                className="grid gap-3 border-t border-gold/15 py-5 last:border-b md:grid-cols-[1fr_auto] md:items-center"
              >
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span
                      data-testid={`admin-status-${art.id}`}
                      className={`px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${
                        published ? "bg-gold/20 text-gold-bright border border-gold/40" : "bg-white/5 text-ivory/50 border border-white/10"
                      }`}
                    >
                      {published ? a.statusPublished : a.statusDraft}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ivory/40">
                      {art.category} · {art.section === "knowledge" ? "Knowledge" : "Insights"} · {formatDate(art.updated_at, lang)}
                    </span>
                  </div>
                  <h3 className="mt-2.5 font-serif text-lg text-ivory">{c?.title}</h3>
                  <p className="mt-1 text-xs text-ivory/40">/{art.slug}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {published && (
                    <Link to={publicUrl} target="_blank" data-testid={`admin-view-${art.id}`} className={`${btn} border-white/15 text-ivory/60 hover:border-gold/50 hover:text-gold-bright`}>
                      <ExternalLink className="h-3 w-3" /> {a.view}
                    </Link>
                  )}
                  <button onClick={() => onEdit(art)} data-testid={`admin-edit-${art.id}`} className={`${btn} border-white/15 text-ivory/70 hover:border-gold/50 hover:text-gold-bright`}>
                    <Pencil className="h-3 w-3" /> {a.edit}
                  </button>
                  <button onClick={() => togglePublish(art)} data-testid={`admin-toggle-${art.id}`} className={`${btn} border-gold/40 text-gold-bright hover:bg-gold hover:text-navy`}>
                    <Eye className="h-3 w-3" /> {published ? a.unpublish : a.publish}
                  </button>
                  <button
                    onClick={() => genVisual(art)}
                    data-testid={`admin-visual-${art.id}`}
                    disabled={busyId === art.id}
                    className={`${btn} border-white/15 text-ivory/60 hover:border-gold/50 hover:text-gold-bright disabled:opacity-50`}
                  >
                    <Sparkles className={`h-3 w-3 ${busyId === art.id ? "animate-pulse" : ""}`} /> {a.visualBtn}
                  </button>
                  <button
                    onClick={() => genPost(art)}
                    data-testid={`admin-social-${art.id}`}
                    disabled={busyPost === art.id}
                    className={`${btn} border-white/15 text-ivory/60 hover:border-gold/50 hover:text-gold-bright disabled:opacity-50`}
                  >
                    <Linkedin className={`h-3 w-3 ${busyPost === art.id ? "animate-pulse" : ""}`} /> {a.socialBtn}
                  </button>
                  <button onClick={() => remove(art)} data-testid={`admin-delete-${art.id}`} className={`${btn} border-red-400/30 text-red-300 hover:bg-red-500/20`}>
                    <Trash2 className="h-3 w-3" /> {a.remove}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {postState && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setPostState(null)}
          data-testid="social-post-modal"
        >
          <div className="w-full max-w-2xl border border-gold/30 bg-navy-light p-7 lg:p-9" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-serif text-2xl text-ivory">{a.socialTitle}</h3>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-ivory/40">/{postState.slug}</p>
              </div>
              <Linkedin className="h-6 w-6 text-gold-bright" />
            </div>
            <p className="mt-4 text-xs leading-relaxed text-ivory/50">{a.socialHint}</p>
            <div className="mt-5 max-h-[45vh] overflow-y-auto border border-gold/20 bg-navy p-5 text-sm leading-relaxed whitespace-pre-wrap text-ivory/80" data-testid="social-post-text">
              {postState.post}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={copyPost}
                data-testid="social-post-copy"
                className="bg-gold px-5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-navy transition-colors hover:bg-gold-bright"
              >
                {a.postCopy}
              </button>
              <button
                onClick={() => setPostState(null)}
                data-testid="social-post-close"
                className="border border-white/15 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/60 transition-colors hover:border-gold/50 hover:text-gold-bright"
              >
                {a.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
